import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, ArrowRight, FileText, Anchor, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function MaritimePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-1273">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-1274">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-1275" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-1276">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-1277">
            Intelligent Maritime Operations <br className="hidden md:block" data-id="element-1278" />
            <span className="text-brand-orange" data-id="element-1279">
              Without the Reporting Burden.
            </span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-1280">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-1281">
              Stop letting manual noon reports, complex compliance
              documentation, and fragmented fleet data slow down your
              operations. We deploy autonomous AI agents that handle reporting,
              compliance checking, and fleet coordination.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-1282">
              If your crew is spending hours on paperwork, they aren't focused
              on the vessel.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-1283">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-1284">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-1285" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-1286">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1287">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1288">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-1289">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-1290">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-1291">
                You're Not Just Operating Vessels.
                <br data-id="element-1292" />
                <span className="text-gray-500" data-id="element-1293">
                  You're Managing Compliance.
                </span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-1294">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-1295">
                Most maritime businesses aren't limited by tonnage — they're
                capped by operational noise. Noon reports compiled manually,
                demurrage disputes escalating unchecked, and compliance
                documentation scattered across email chains and spreadsheets.
              </motion.p>

              <div className="space-y-8" data-id="element-1296">
                {[{
                label: 'The Noon Report Gap',
                desc: 'Vessel data is manually compiled, emailed ashore, and re-keyed into systems — introducing delays and errors at every step.'
              }, {
                label: 'The Demurrage Trap',
                desc: 'Laytime calculations happen reactively, disputes surface weeks after the fact, and thousands are lost to poor documentation.'
              }, {
                label: 'The Compliance Bottleneck',
                desc: 'IMO, MARPOL, and flag state requirements demand constant documentation — your operations team spends more time on paperwork than on performance.'
              }].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1297">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-1298">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-1299" />
                    </div>
                    <div data-id="element-1300">
                      <p className="text-lg text-white font-medium mb-1" data-id="element-1301">
                        {point.label}
                      </p>
                      <p className="text-gray-400 font-light leading-relaxed" data-id="element-1302">
                        {point.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-1303">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1304">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-1305">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1306">
              Purpose-Built Agents for Maritime Workflows.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-1307">
              We don't sell AI tools. We build autonomous digital operators that
              understand maritime regulations, access your fleet management
              systems, and execute real reporting without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-1308">
            {[{
            icon: <FileText className="w-6 h-6 text-brand-orange" data-id="element-1309" />,
            title: 'Automated Reporting',
            desc: 'Agents instantly extract data from vessel sensors and logs to generate accurate noon reports, emissions data, and voyage summaries.'
          }, {
            icon: <CheckCircle2 className="w-6 h-6 text-brand-orange" data-id="element-1310" />,
            title: 'Compliance Checking',
            desc: 'Automatically cross-references vessel operations against MARPOL, ISM, and local port regulations to ensure continuous compliance.'
          }, {
            icon: <Anchor className="w-6 h-6 text-brand-orange" data-id="element-1311" />,
            title: 'Port Orchestration',
            desc: 'Coordinates arrival documentation, pilot bookings, and berth availability, updating all parties dynamically as ETAs change.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-1312">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-1313">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-1314">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1315">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1316">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1317">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-1318">
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-1319" />

            {/* Left: Traditional Model */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-1320">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-1321">
                Traditional Model
              </h3>
              <div className="space-y-10" data-id="element-1322">
                {['Chief Officer spends 45 minutes compiling the noon report — data re-keyed ashore with errors', 'Port call coordination requires 20+ emails between agent, terminal, and operations desk', 'Laytime dispute surfaces 3 weeks post-discharge — documentation is incomplete and scattered'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-1323">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-1324" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-1325">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: Aijentik Model */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-1326">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-1327">
                Aijentik Model
              </h3>
              <div className="space-y-10" data-id="element-1328">
                {['Noon report data extracted from vessel systems automatically — validated and filed in under 60 seconds', 'Port call orchestrated end-to-end: berth confirmed, agents notified, ETA updates pushed in real-time', 'Laytime tracked continuously from NOR to completion — demurrage calculated and documented proactively'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-1329">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-1330" />
                    <p className="text-xl text-white font-medium" data-id="element-1331">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. USE CASES SECTION */}
      <section className="py-32" data-id="element-1332">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1333">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-1334">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-1335" />
            <span className="text-gray-500" data-id="element-1336">in Your Operations</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-1337">
            {[{
            title: 'Zero-Touch Noon Reports',
            desc: 'Vessel data is automatically compiled, validated, and submitted to shore-side systems without crew intervention.'
          }, {
            title: 'Predictive Maintenance Alerts',
            desc: 'Monitors equipment telemetry to predict failures and automatically orders required spares to the next suitable port.'
          }, {
            title: 'Automated Charter Party Compliance',
            desc: 'Continuously tracks vessel performance against charter party agreements, flagging potential claims before they occur.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-1338">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-1339">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1340">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1341">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1342">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-1343">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1344">
              No Fluff. Just Execution.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-1345">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-1346" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your reporting workflows, compliance requirements, and identify the manual touchpoints slowing your fleet.'
          }, {
            step: '02',
            title: 'Build',
            desc: 'We train custom agents on your specific vessel types, regulatory frameworks, and operational systems.'
          }, {
            step: '03',
            title: 'Integration',
            desc: 'We connect agents directly to your fleet management software and communication channels for seamless orchestration.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-1347">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-1348">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-1349">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-1350">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-1351">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-1352" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-1353" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-1354">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-1355">
            Stop Managing Paperwork. <br data-id="element-1356" />
            <span className="text-brand-orange" data-id="element-1357">Start Scaling Operations.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-1358">
            Let's look at your fleet operations and identify automation
            opportunities that increase your efficiency.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-1359">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-1360">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1361">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}